import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

import { request } from './APIClient';
import { createLogger } from './logger';
import { dataPointToMidiEvents, DataPoint } from './Player';
import { scheduleMIDINote } from './MIDIPlayer';
import { PitchSettings } from '../domain/pitch';
import { VelocitySettings } from '../domain/velocity';
import { CategoryRouting } from '../domain/categoryRouting';
import { toISOStringNoMs } from './time';

// ============================================================
// Types
// ============================================================

/**
 * Options for processQueue.
 */
export interface ProcessQueueOptions {
  /** Current time (fake "now" in testing, real now in production) */
  currentTime: Dayjs;
  /** Playback starts as if now was this far into the past (seconds) */
  offset: number;
  /** Minimum number of datapoints to slurp in from the server */
  inboundBufferSize: number;
  /** Number of datapoints to transform into midi at a time */
  batchSize: number;
  /** Time window into the future for fetching (seconds) */
  duration: number;
  /** Maximum time in the future to look ahead (seconds) */
  lookAheadTime: number;
  /** Optional pitch mapping settings */
  pitchSettings?: PitchSettings;
  /** Optional velocity mapping settings */
  velocitySettings?: VelocitySettings;
  /** Optional category -> {enabled, port, channel} mapping */
  categoryRouting?: CategoryRouting;
  /** Optional staccato note length in ms */
  noteLengthMs?: number;
  /** Optional random extra delay for note start (ms) */
  noteStartJitterMs?: number;
  /** Cap to prevent memory leak (default: 10000) */
  maxProcessedQueueSize?: number;
}

/**
 * Result from processQueue.
 */
export interface ProcessQueueResult {
  processedDataQueue: DataPoint[];
  dataQueue: DataPoint[];
  from: Dayjs;
}

/**
 * Read-only queue state for realtime visualizations.
 */
export interface QueueGraphSnapshot {
  processedDataQueue: DataPoint[];
  playbackHeadTimeIso: string | null;
  categoryRouting: CategoryRouting | null;
}

/**
 * Function type for fetching next batch of data.
 */
export type NextBatchFn = (from: Dayjs, duration: number) => Promise<DataPoint[]>;

/**
 * Function type for scheduling MIDI notes.
 */
export type ScheduleMIDINoteFn = (
  port: number | string,
  channel: number | string,
  note: number,
  velocity: number,
  when: Date | number,
  durationMs: number
) => Promise<void>;

// ============================================================
// State
// ============================================================

const log = createLogger('queue');

let dataQueue: DataPoint[] = [];
let processedDataQueue: DataPoint[] = [];
let from: Dayjs | null = null;
let playbackHeadTimeIso: string | null = null;
let latestCategoryRouting: CategoryRouting | null = null;

// Default max size for processedDataQueue to prevent unbounded memory growth.
const DEFAULT_MAX_PROCESSED_QUEUE_SIZE = 10000;

// ============================================================
// Functions
// ============================================================

/**
 * Reset all queue state.
 */
export function resetQueue(): void {
  dataQueue = [];
  processedDataQueue = [];
  from = null;
  playbackHeadTimeIso = null;
  latestCategoryRouting = null;
}

function cloneCategoryRouting(routing: CategoryRouting): CategoryRouting {
  const cloned: CategoryRouting = {};
  for (const [categoryId, route] of Object.entries(routing)) {
    cloned[categoryId] = { ...route };
  }
  return cloned;
}

/**
 * Return a snapshot for UI graphing without exposing mutable queue references.
 */
export function getQueueGraphSnapshot(): QueueGraphSnapshot {
  return {
    processedDataQueue: [...processedDataQueue],
    playbackHeadTimeIso,
    categoryRouting: latestCategoryRouting ? cloneCategoryRouting(latestCategoryRouting) : null,
  };
}

/**
 * Fetch next batch of data from the API.
 */
async function nextBatch(fromTime: Dayjs, duration: number): Promise<DataPoint[]> {
  log.debug('fetching next batch', fromTime, duration);
  const to = fromTime.add(duration, 'seconds');
  const response = await request('/data?from=' + toISOStringNoMs(fromTime) + '&to=' + toISOStringNoMs(to));
  return response.json();
}

/**
 * Process the data queue: fetch data, transform to MIDI events, and schedule.
 *
 * @param options - Processing options
 * @param nextBatchOverride - Optional override for data fetching (for testing)
 * @param scheduleMIDINoteOverride - Optional override for MIDI scheduling (for testing)
 * @returns Processing result with queue state
 */
export async function processQueue(
  options: ProcessQueueOptions,
  nextBatchOverride?: NextBatchFn,
  scheduleMIDINoteOverride?: ScheduleMIDINoteFn
): Promise<ProcessQueueResult> {
  const { 
    currentTime,
    offset,
    inboundBufferSize,
    batchSize,
    duration,
    lookAheadTime,
    pitchSettings,
    velocitySettings,
    categoryRouting,
    noteLengthMs,
    noteStartJitterMs,
    maxProcessedQueueSize = DEFAULT_MAX_PROCESSED_QUEUE_SIZE,
  } = options;

  const fetchNextBatch: NextBatchFn = nextBatchOverride ?? nextBatch;
  const scheduleMIDINoteFunc: ScheduleMIDINoteFn = scheduleMIDINoteOverride ?? scheduleMIDINote;
  latestCategoryRouting = categoryRouting ? cloneCategoryRouting(categoryRouting) : null;

  // "playback head" is currentTime shifted back by offset into the original timeseries
  const playbackHeadTime = currentTime.subtract(offset, 'seconds');
  playbackHeadTimeIso = playbackHeadTime.toISOString();
  from = from === null ? playbackHeadTime : from;
 
  log.debug('from', from);
  log.debug('currentTime', currentTime);
  log.debug('offset', offset);
  log.debug('inboundBufferSize', inboundBufferSize);
  log.debug('batchSize', batchSize);
  log.debug('duration', duration);
  log.debug('nextBatchOverride', nextBatchOverride);
  log.debug('options', options);
  log.debug('fetchNextBatch', fetchNextBatch);

  // Fetch more data if buffer is low.
  // Compare in the ORIGINAL data timeline: only fetch if we're within lookAheadTime of the playback head.
  if (from.isBefore(playbackHeadTime.add(lookAheadTime, 'seconds'))) {
    while (dataQueue.length < inboundBufferSize) {
      const newData = await fetchNextBatch(from, duration);
      if (!newData || newData.length === 0) {
        break;
      }

      from = dayjs.utc(newData[newData.length - 1].time);
      log.debug('from (after fetch)', from);

      dataQueue.push(...newData);
      log.debug('Buffer refilled. Queue length:', dataQueue.length);
    }
  }

  log.debug('dataQueue', dataQueue);

  // Process events if we have data
  while (dataQueue.length > 0) {
    const batch = dataQueue.length > batchSize 
      ? dataQueue.splice(0, batchSize) 
      : dataQueue.splice(0, dataQueue.length);
    log.debug('batch', batch);
    
    for (const dataPoint of batch) {
      // Apply offset so historic datapoints are replayed as-if starting "now"
      const result = dataPointToMidiEvents(
        dataPoint,
        currentTime,
        categoryRouting,
        offset,
        pitchSettings,
        velocitySettings,
        noteLengthMs,
        noteStartJitterMs
      );
      log.debug('result', result);

      for (const midiEvent of result) {
        await scheduleMIDINoteFunc(
          midiEvent.port,
          midiEvent.channel,
          midiEvent.note,
          midiEvent.velocity,
          midiEvent.when,
          midiEvent.durationMs
        );
      }
    }

    processedDataQueue.push(...batch);

    // Sliding window: trim oldest items if we exceed maxProcessedQueueSize.
    if (processedDataQueue.length > maxProcessedQueueSize) {
      const excess = processedDataQueue.length - maxProcessedQueueSize;
      processedDataQueue.splice(0, excess);
    }
  }
    
  return { processedDataQueue, dataQueue, from };
}

/**
 * Calculate the offset in seconds between two times.
 *
 * @param currentTime - The current time
 * @param startDatetime - The start time
 * @returns Offset in seconds
 */
export function calculateOffset(currentTime: Dayjs, startDatetime: Dayjs): number {
  return currentTime.diff(startDatetime, 'seconds');
}

